<template>
  <div class="assigned-users">
    <!-- Single user: avatar + name -->
    <span v-if="normalizedUsers.length === 1" class="assigned-users__person" :title="getUserDisplayName(normalizedUsers[0])">
      <span class="assigned-users__avatar" :style="avatarStyle(normalizedUsers[0])" aria-hidden="true">
        <img v-if="getAvatarUrl(normalizedUsers[0])" :src="getAvatarUrl(normalizedUsers[0])" alt="" @error="markImageFailed(getAvatarUrl(normalizedUsers[0]))" />
        <template v-else>{{ getInitialsFromUser(normalizedUsers[0]) }}</template>
      </span>
      <span class="assigned-users__name">{{ getUserDisplayName(normalizedUsers[0]) }}</span>
    </span>

    <!-- Several users: one stack, one tooltip listing everyone -->
    <v-tooltip v-else-if="normalizedUsers.length > 1" bottom>
      <template v-slot:activator="{ on, attrs }">
        <span
          v-bind="attrs"
          v-on="on"
          class="assigned-users__stack"
          :style="{ '--avatar-ring': ringColor }"
          tabindex="0"
          role="img"
          :aria-label="stackLabel">
          <span
            v-for="(user, index) in stack.shown"
            :key="getUserKey(user, index)"
            class="assigned-users__avatar"
            :style="avatarStyle(user)"
            aria-hidden="true">
            <img v-if="getAvatarUrl(user)" :src="getAvatarUrl(user)" alt="" @error="markImageFailed(getAvatarUrl(user))" />
            <template v-else>{{ getInitialsFromUser(user) }}</template>
          </span>
          <span
            v-if="stack.overflow"
            class="assigned-users__avatar assigned-users__overflow"
            :style="neutralStyle"
            aria-hidden="true">+{{ stack.overflow }}</span>
        </span>
      </template>
      <div class="assigned-users__tooltip">
        <div class="assigned-users__tooltip-title">{{ normalizedUsers.length }} assigned</div>
        <div
          v-for="(user, index) in normalizedUsers"
          :key="`tip-` + getUserKey(user, index)"
          class="assigned-users__tooltip-row">
          <span class="assigned-users__avatar assigned-users__avatar--small" :style="avatarStyle(user)" aria-hidden="true">
            <img v-if="getAvatarUrl(user)" :src="getAvatarUrl(user)" alt="" @error="markImageFailed(getAvatarUrl(user))" />
            <template v-else>{{ getInitialsFromUser(user) }}</template>
          </span>
          <span>{{ getUserDisplayName(user) }}</span>
        </div>
      </div>
    </v-tooltip>

    <!-- Nobody assigned: em-dash, like every other empty table cell -->
    <span v-else class="assigned-users__empty" aria-label="No users assigned">—</span>
  </div>
</template>

<script>
  import {
    getAvatarColors,
    getInitials,
    getNeutralAvatarColors,
    splitAvatarStack,
  } from '@/features/Share/utils/userAvatarPalette';

  export default {
    name: 'AssignedUsersCell',
    props: {
      users: {
        type: Array,
        default: () => [],
      },
      max: {
        type: Number,
        default: 3,
      },
    },
    data() {
      return {
        // Photo URLs that failed to load (expired SAS, storage offline): fall back to initials.
        failedImages: {},
      };
    },
    computed: {
      isDark() {
        return Boolean(this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark);
      },
      normalizedUsers() {
        // Accept array of UserVM objects or string IDs. If string IDs, show placeholder.
        return (this.users || []).map((u) => {
          if (u && typeof u === 'object') return u;
          return { id: u, firstName: 'User', lastName: '' };
        });
      },
      stack() {
        return splitAvatarStack(this.normalizedUsers, this.max);
      },
      stackLabel() {
        return `Assigned: ${this.normalizedUsers.map((user) => this.getUserDisplayName(user)).join(', ')}`;
      },
      // The ring matches the table body it sits on (Vuetify light #fff, dark #1e1e1e).
      ringColor() {
        return this.isDark ? '#1e1e1e' : '#ffffff';
      },
      neutralStyle() {
        const { bg, fg } = getNeutralAvatarColors(this.isDark);
        return { backgroundColor: bg, color: fg };
      },
    },
    methods: {
      getUserKey(user, index) {
        return user.id || user.userId || index;
      },
      getAvatarUrl(user) {
        const url = user.avatarUrl || user.profileImageUrl || user.imageUrl || '';
        return url && !this.failedImages[url] ? url : '';
      },
      markImageFailed(url) {
        if (url) this.$set(this.failedImages, url, true);
      },
      getUserDisplayName(user) {
        const name = user.name || user.fullName || user.displayName;
        if (name) return name;
        const first = user.firstName || '';
        const last = user.lastName || '';
        const fallback = (user.email || '').split('@')[0];
        return (first + ' ' + last).trim() || fallback || 'User';
      },
      getInitialsFromUser(user) {
        return getInitials(this.getUserDisplayName(user));
      },
      avatarStyle(user) {
        const { bg, fg } = getAvatarColors(user.id || user.userId || this.getUserDisplayName(user), this.isDark);
        return { backgroundColor: bg, color: fg };
      },
    },
  };
</script>

<style scoped>
  .assigned-users {
    display: flex;
    align-items: center;
    min-height: 28px;
    min-width: 0;
  }

  .assigned-users__avatar {
    width: 28px;
    height: 28px;
    flex: none;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.02em;
    user-select: none;
  }

  .assigned-users__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .assigned-users__avatar--small {
    width: 20px;
    height: 20px;
    font-size: 9px;
  }

  .assigned-users__person {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    max-width: 100%;
  }

  .assigned-users__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  .assigned-users__stack {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    outline: none;
  }

  /* A surface-coloured ring separates overlapping circles; no border, no shadow, no hover lift. */
  .assigned-users__stack .assigned-users__avatar {
    box-shadow: 0 0 0 2px var(--avatar-ring, #fff);
  }

  .assigned-users__stack .assigned-users__avatar + .assigned-users__avatar {
    margin-left: -6px;
  }

  .assigned-users__stack:focus-visible {
    box-shadow: 0 0 0 3px rgba(3, 98, 76, 0.32);
  }

  .assigned-users__overflow {
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
  }

  .assigned-users__empty {
    opacity: 0.5;
  }

  .assigned-users__tooltip {
    max-width: 260px;
  }

  .assigned-users__tooltip-title {
    font-size: 11px;
    font-weight: 600;
    opacity: 0.7;
    margin-bottom: 6px;
  }

  .assigned-users__tooltip-row {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .assigned-users__tooltip-row + .assigned-users__tooltip-row {
    margin-top: 6px;
  }
</style>
